import { OrgRole, TeamPermission } from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/company';

import { TheraManagerExtraPermissions, TheraOrgRoles, TheraTeamRoles } from 'lib/constants';
import { useUser } from 'context/user';
import { PROD_SUPPORT_ZEBRA_COMPANY_ID, SZ_COORDINATOR_USER_ID } from 'lib/whitelistConstants';

/** Only Team Managers and Team Admins can create or edit invoices */
export const usePermissions = () => {
  const { affiliationsByCompany, currentCompany, user } = useUser() || {};
  const companyId = currentCompany?.companyID;
  // make exceptions for SZ_COORDINATOR_USER_ID and PROD_SUPPORT_ZEBRA_COMPANY_ID
  const isProdSupportZebra = companyId === PROD_SUPPORT_ZEBRA_COMPANY_ID;
  const isSZCoordinator = isProdSupportZebra && user?.userID === SZ_COORDINATOR_USER_ID;

  const companyPermissions = affiliationsByCompany?.[companyId];
  // Check if orgRoleAndDetails has only one role and it is role_admin
  const isOrgAdmin =
    companyPermissions?.orgRoleAndDetails?.length === 1 &&
    companyPermissions.orgRoleAndDetails[0].role === TheraOrgRoles.role_admin
      ? true
      : false;

  /**
   * HR specialist can perform all actions except paying contractors and adding other managers with pay permissions (i.e Team Admin and People Manager and Payer)
   */
  const isHRSpecialist = companyPermissions?.orgRoleAndDetails?.some(
    (roleDetail) => roleDetail.role === OrgRole.RoleOrgManager,
  );

  // Check if orgRoleAndDetails has only one role and it is role_apar_manager
  const isOnlyAPARManager =
    companyPermissions?.orgRoleAndDetails?.length === 1 &&
    companyPermissions.orgRoleAndDetails[0].role === TheraOrgRoles.role_apar_manager
      ? true
      : false;

  // Check if orgRoleAndDetails contains role_apar_manager (regardless of other roles)
  const isAPARManager = companyPermissions?.orgRoleAndDetails?.some(
    (roleDetail) => roleDetail.role === TheraOrgRoles.role_apar_manager,
  );

  /**
   * Only Org Admins can add or remove payment methods, change payment method details, etc.
   */
  const canManagePaymentMethods = isOrgAdmin;
  /**
   * Only Org Admins can manage managers - add or remove members, change roles, etc.
   */
  const canManageManagers = isOrgAdmin;
  /**
   * Only Org Admins can manage teams - add or remove members, change team name, etc.
   */
  const canManageTeams = isOrgAdmin || isHRSpecialist;
  const isOnlyViewer = () => {
    // Org admins and HR specialists are never viewers
    if (isOrgAdmin || isHRSpecialist) return false;

    const teams = companyPermissions?.teams;
    // check if for any team he has the role - role_people_manager or role_people_manager_and_payer or role_admin
    if (!teams) return true;
    const teamIds = Object.keys(teams);
    if (!teamIds?.length) return true;
    const nonViewerRoles = ['role_people_manager', 'role_people_manager_and_payer', 'role_admin'];
    // check if for at least one team he has the nonViewerRoles role
    const hasAtLeastOneNonViewerRole = teamIds.some((teamId) => {
      const role = teams[teamId].role;
      return nonViewerRoles.includes(role);
    });
    return !hasAtLeastOneNonViewerRole;
  };

  const canAddEmployee = isOrgAdmin || isHRSpecialist ? true : !isOnlyViewer();

  const canManageSettings = isOrgAdmin || isHRSpecialist;

  const isOrgManager = companyPermissions?.orgRoleAndDetails?.some(
    (roleDetail) => roleDetail.role === TheraOrgRoles.role_manager,
  );

  // Org admin, org manager, HR specialist, and people managers on the worker's own team(s) can
  // edit onboarding worker email/name. APAR roles are intentionally excluded. Kept separate from
  // canManageSettings so loosening this gate doesn't widen unrelated flows.
  const canEditOnboardingWorkerNameAndEmail = (workerTeamIds: string[]) =>
    companyPermissions?.orgRoleAndDetails?.some((r) => r.role === TheraOrgRoles.role_admin) ||
    Boolean(isOrgManager) ||
    Boolean(isHRSpecialist) ||
    workerTeamIds.some(
      (teamId) =>
        companyPermissions?.teams[teamId]?.role === TheraTeamRoles.role_people_manager ||
        companyPermissions?.teams[teamId]?.role === TheraTeamRoles.role_people_manager_and_payer,
    );

  /**
   * Except Viewers, anyone now can manage the team
   **/
  const canManageTeamById = (teamId: string) =>
    companyPermissions?.teams[teamId]?.role !== TheraTeamRoles.role_viewer_sensitive;

  /**
   * Only Team Admins/Managers with permissions can review invoices for their own team
   **/
  const canReviewInvoiceByTeamId = (teamId: string) => {
    if (isOrgAdmin || isHRSpecialist) return true;
    if (isSZCoordinator) return false;
    return (
      // if Team Admin
      companyPermissions?.teams[teamId]?.role === TheraTeamRoles.role_admin ||
      // if Team Manager/Payer but have permission to review (approve/reject) invoices
      ((companyPermissions?.teams[teamId]?.role === TheraTeamRoles.role_people_manager ||
        companyPermissions?.teams[teamId]?.role === TheraTeamRoles.role_people_manager_and_payer) &&
        companyPermissions?.teams[teamId]?.permissions?.includes(
          TheraManagerExtraPermissions.perm_approve_payroll_and_payments.id as TeamPermission,
        ))
    );
  };

  /**
   * Timesheet approval (approve, reject, reopen, edit entries) for the employee's team. Kept apart
   * from invoice review so team-level HR Specialists can approve hours without widening invoices.
   **/
  const canApproveTimesheetByTeamId = (teamId: string) => {
    if (isOrgAdmin || isHRSpecialist) return true;
    if (isSZCoordinator) return false;
    const team = companyPermissions?.teams[teamId];
    // Team Admin or team-level HR Specialist
    if (team?.role === TheraTeamRoles.role_admin || team?.role === TheraTeamRoles.role_org_manager) return true;
    // People Manager with the approve permission
    return (
      (team?.role === TheraTeamRoles.role_people_manager ||
        team?.role === TheraTeamRoles.role_people_manager_and_payer) &&
      Boolean(
        team?.permissions?.includes(
          TheraManagerExtraPermissions.perm_approve_payroll_and_payments.id as TeamPermission,
        ),
      )
    );
  };

  /**
   * Whether a timesheet's contract sits on one of the viewer's own teams. Org Admin and org-level HR
   * see every team. Display filter only: the backend still returns the whole company's timesheets.
   **/
  const isTimesheetContractOnMyTeams = (contractId: string, teams: Record<string, { contractIds?: string[] }>) => {
    if (isOrgAdmin || isHRSpecialist) return true;
    return Object.keys(companyPermissions?.teams ?? {}).some((teamId) =>
      teams?.[teamId]?.contractIds?.includes(contractId),
    );
  };

  /**
   * Only Team Admins/Managers with permissions can deny invoices for their own team
   **/
  const canDenyInvoiceByTeamId = (teamId: string) => {
    if (isOrgAdmin || isHRSpecialist) return true;
    return (
      // if Team Admin
      companyPermissions?.teams[teamId]?.role === TheraTeamRoles.role_admin ||
      // if Team Manager/Payer but have permission to review (approve/reject) invoices
      ((companyPermissions?.teams[teamId]?.role === TheraTeamRoles.role_people_manager ||
        companyPermissions?.teams[teamId]?.role === TheraTeamRoles.role_people_manager_and_payer) &&
        companyPermissions?.teams[teamId]?.permissions?.includes(
          TheraManagerExtraPermissions.perm_approve_payroll_and_payments.id as TeamPermission,
        ))
    );
  };

  const canPayInvoiceByTeamId = (teamId: string) => {
    if (isOrgAdmin) return true;
    return (
      companyPermissions?.teams[teamId]?.role === TheraTeamRoles.role_admin ||
      companyPermissions?.teams[teamId]?.role === TheraTeamRoles.role_people_manager_and_payer
    );
  };

  const canCreateEditInvoiceByTeamId = (teamId: string) => {
    if (isOrgAdmin || isHRSpecialist) return true;
    return (
      // if Team Admin
      companyPermissions?.teams[teamId]?.role === TheraTeamRoles.role_admin ||
      // if Team Manager/Payer but have permission to review (approve/reject) invoices
      ((companyPermissions?.teams[teamId]?.role === TheraTeamRoles.role_people_manager ||
        companyPermissions?.teams[teamId]?.role === TheraTeamRoles.role_people_manager_and_payer) &&
        companyPermissions?.teams[teamId]?.permissions?.includes(
          TheraManagerExtraPermissions.perm_adjust_payroll_and_payments.id as TeamPermission,
        ))
    );
  };

  const isViewerByTeamId = (teamId: string) =>
    companyPermissions?.teams[teamId]?.role === TheraTeamRoles.role_viewer_sensitive;

  return {
    isOrgAdmin,
    isHRSpecialist,
    companyPermissions,
    canManageManagers,
    canManagePaymentMethods,
    canManageTeams,
    canManageTeamById,
    canReviewInvoiceByTeamId,
    canApproveTimesheetByTeamId,
    isTimesheetContractOnMyTeams,
    canDenyInvoiceByTeamId,
    canPayInvoiceByTeamId,
    canCreateEditInvoiceByTeamId,
    isViewerByTeamId,
    canManageSettings,
    canEditOnboardingWorkerNameAndEmail,
    canAddEmployee,
    isOnlyViewer,
    isOnlyAPARManager,
    isAPARManager,
  };
};
